<template>
  <div
    class="form-control-item form-control-item--text"
    v-if="['text', 'number', 'email', 'tel'].includes(item.type)"
  >
    <validation-provider
      :rules="item.validation"
      v-slot="{ errors, invalid, touched }"
    >
      <label :for="item.key">
        <span v-if="typeof item.label === 'function'">
          {{ item.label() }}
        </span>
        <span v-else> {{ item.label }} </span
        ><span v-if="item.required">*</span>
      </label>
      <input
        v-model="item.value"
        :name="item.key"
        :placeholder="item.placeholder"
        :type="item.type"
        :minlength="item.minlength"
        :maxlength="item.maxlength"
        :class="[
          { invalid: invalid },
          { touched: touched },
          { 'has-errors': errors.length > 0 },
        ]"
        :disabled="item.disabled"
      />
      <span class="validation-label">
        {{ errors[0] }}
        <span v-if="errors.length > 0 && item.hint">({{ item.hint }})</span>
      </span>
      <p v-if="errors.length === 0 && item.hint" class="mobile-phone">
        {{ item.hint }}
      </p>
    </validation-provider>
  </div>
  <div
    class="form-control-item form-control-item--large-text"
    v-else-if="item.type === 'largetext'"
  >
    <validation-provider :rules="item.validation" v-slot="{ errors }">
      <label>{{ item.label }}<span v-if="item.required">*</span></label>
      <textarea v-model="value" :placeholder="item.placeholder" />
      <span class="validation-label">{{ errors[0] }}</span>
    </validation-provider>
  </div>
  <password-control
    :item="item"
    stylingClass="form-control-item form-control-item--password"
    v-else-if="item.type === 'password'"
  />
  <div
    class="form-control-item form-control-item--checkbox"
    v-else-if="item.type === 'checkbox'"
  >
    <template v-if="item.options.length <= 0">
      <validation-provider :rules="item.validation" v-slot="{ errors }">
        <input
          v-model="item.value"
          :placeholder="item.placeholder"
          :id="`${item.key}-${item.id}`"
          :name="item.id"
          type="checkbox"
        />
        <label :for="`${item.key}-${item.id}`">{{ item.label }}</label>
        <span class="validation-label">{{ errors[0] }}</span>
      </validation-provider>
    </template>
    <template v-else-if="item.options.length > 0">
      <label class="label-wrapper">{{ item.label }}</label>
      <validation-provider :rules="item.validation" v-slot="{ errors }">
        <div
          v-for="option in item.options"
          :key="option.value"
          class="form-control-item__group"
        >
          <input
            v-model="item.value"
            :placeholder="option.placeholder"
            :id="`${option.value}-${item.id}`"
            :name="item.id"
            type="checkbox"
          />
          <label :for="`${option.value}-${item.id}`">
            {{ option.text }}
          </label>
        </div>
        <span class="validation-label">{{ errors[0] }}</span>
      </validation-provider>
    </template>
  </div>
  <div
    class="form-control-item form-control-item--radio"
    v-else-if="item.type === 'radio'"
  >
    <template v-if="item.options.length <= 0">
      <validation-provider :rules="item.validation" v-slot="{ errors }">
        <input
          v-model="item.value"
          :placeholder="item.placeholder"
          :id="`${item.key}-${item.id}`"
          :name="item.id"
          type="radio"
        />
        <label :for="`${item.key}-${item.id}`">{{ item.label }}</label>
        <span class="validation-label">{{ errors[0] }}</span>
      </validation-provider>
    </template>
    <template v-if="item.options.length > 0">
      <validation-provider :rules="item.validation" v-slot="{ errors }">
        <label class="label-wrapper">
          {{ item.label }}
          <span v-if="item.required">*</span>
        </label>
        <div
          v-for="option in item.options"
          :key="option.value"
          class="form-control-item__group"
        >
          <input
            v-model="item.value"
            :placeholder="option.placeholder"
            :id="`${option.value}-${item.id}`"
            :value="option.value"
            :name="item.id"
            :selected="option.value === item.value"
            type="radio"
          />
          <label :for="`${option.value}-${item.id}`">
            {{ option.text }}
          </label>
        </div>
        <span class="validation-label">{{ errors[0] }}</span>
      </validation-provider>
    </template>
  </div>
  <div
    class="form-control-item form-control-item--dropdown"
    v-else-if="item.type === 'dropdown'"
  >
    <validation-provider
      :rules="item.validation"
      v-slot="{ errors, invalid, touched }"
    >
      <label v-if="!item.hideLabel" :for="`${item.key}-${item.id}`">
        <span v-if="typeof item.label === 'function'">
          {{ item.label() }}
        </span>
        <span v-else> {{ item.label }}</span>
        <span v-if="item.required">*</span>
      </label>
      <div class="select-box">
        <select
          :id="`${item.key}`"
          :name="item.key"
          v-model="item.value"
          :class="[
            { invalid: invalid },
            { touched: touched },
            { 'has-errors': errors.length > 0 },
          ]"
          :disabled="item.disabled"
        >
          <option v-if="!item.hideLabel" value="" selected disabled hidden>
            Please select...
          </option>
          <option v-else value="" selected disabled hidden>
            {{ item.label }}{{ item.required ? "*" : "" }}
          </option>
          <option
            v-for="option in item.options"
            :key="option.value"
            :value="option.value"
            :selected="item.value === option.value"
          >
            {{ option.text }}
          </option>
        </select>
      </div>
      <span class="validation-label">{{ errors[0] }}</span>
    </validation-provider>
  </div>
  <date-picker
    :item="item"
    stylingClass="form-control-item form-control-item--datepicker"
    v-else-if="item.type === 'date'"
    @datePicked="handleDateSelected"
    @dateInvalid="invalidForm"
  />
  <Address :item="item" v-else-if="item.type === 'address'" />
  <WhereFound :item="item" v-else-if="item.type === 'whereFound'" />
  <UniversitySelect :item="item" v-else-if="item.type === 'university'" />
</template>

<script>
import Address from "@/components/Address";
import DatePicker from "@/components/DatePicker.vue";
import PasswordControl from "@/components/PasswordControl";
import UniversitySelect from "@/components/UniversitySelect.vue";
import WhereFound from "@/components/WhereFound.vue";
import { registrationConfig } from "@/helpers/config";
import { ValidationProvider, extend } from "vee-validate";
import {
  alpha_num,
  alpha_spaces,
  confirmed,
  email,
  max,
  min,
  numeric,
  oneOf,
  required,
} from "vee-validate/dist/rules";
extend("required", {
  ...required,
  message: "This field is required",
});

extend("numeric", {
  ...numeric,
  message: "This field can only contain digits",
});

extend("email", {
  ...email,
  message: "Please enter a valid email address",
});

extend("min", {
  ...min,
  message: "Not enough characters",
});

extend("max", {
  ...max,
  message: "Too many characters",
});

extend("text", {
  ...alpha_spaces,
  message: "This field may only contain text and spaces",
});

extend("oneOf", {
  ...oneOf,
  message: "Please select a valid country",
});

extend("alphanumeric", {
  ...alpha_num,
  message: "This field may only contain text and numbers",
});

extend("phoneNumValidate", {
  validate: (value) => /^(\+\d{11,14}|\d{11,14})$/.test(value),
  message: "Invalid phone number",
});

extend("hcpc", {
  validate: (value) => {
    if (registrationConfig.testHCPCEnabled)
      return /^RA\d{5,6}$/.test(value) || /^TRA\d{4,5}$/.test(value);

    return /^RA\d{5,6}$/.test(value);
  },
  getMessage: () => "Invalid HCPC number",
});

extend("confirmed", {
  ...confirmed,
  message: "Fields do not match",
});

export default {
  props: ["item", "fields"],
  name: "FormFieldRenderer",
  components: {
    ValidationProvider,
    PasswordControl,
    DatePicker,
    Address,
    WhereFound,
    UniversitySelect,
  },
  methods: {
    handleDateSelected(date, key) {
      this.fields.forEach((f) => {
        if (f.type === "date" && f.key === key) {
          f.value = date;
        }
      });
      this.$emit("validForm");
    },
    invalidForm() {
      this.$emit("invalidForm");
    },
  },
};
</script>
